import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import s from './ModelServicePlan.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelState } from '../../reducers/initialState';
import ModelServicePlanListTable from '../ModelServicePlan/ModelServicePlanListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { compareValues } from '../../utils';
import Cookies from 'js-cookie';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { getModelCategoryPermission } from '../../utils';
import { useParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';


const ModelServicePlansList = ({
  servicePlans = { ...initialModelState.modelManager.selectedModel.ServicePlans },
  intl,
  ReadOnly,
  userProfileWidgets,
  checklistTemplate,
  getModelServicePlans,
  saveModelServicePlan,
  describeModelServicePlan,
  changeModelServicePlan,
  setServicePlanShowScreenStatus,
  getChecklistTemplate,
  userModelCategories,
  currentTimezone,
  setCurrentPage,

}) => {

  let { ModelCategoryId, ModelId } = useParams();

  let filter = '';
  let offset = 0;
  let limit = 1000;

  useEffect(() => {
    getModelServicePlans(offset, limit, filter, ModelCategoryId, ModelId);
  }, [getModelServicePlans, offset, limit, filter, ModelCategoryId, ModelId]);

  const [currentEditId, setCurrentEditId] = useState(-1);
  const [active, setActive] = useState(false);
  const [servicePlanId, setServicePlanId] = useState(0);
  const [searchText, setSearchText] = useState('');

  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  const addNewRef = useRef(null);

  useEffect(() => {
    getChecklistTemplate('MODELSERVICEPLANSADD');
  }, [getChecklistTemplate]);


  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };


  const searchHandler = value => {
    setCurrentPage(1);
    setSearchText(value);
  };

  const disableModeHandler = status => {
    setServicePlanShowScreenStatus(status);
  };


  const setAddHandler = () => {
    setServicePlanShowScreenStatus(true);
    setCurrentEditId(0);
    window.scrollTo(0, addNewRef.current.offsetTop);
  };

  const setEditHandler = modelServicePlanId => {
    setCurrentEditId(modelServicePlanId);
    if (modelServicePlanId === -1) {
      changeModelServicePlan({
        SelectedServicePlan: {
          ...initialModelState.modelManager.selectedModel.ServicePlans.SelectedServicePlan,
          ModelId: ModelId
        }
      });
      setServicePlanShowScreenStatus(false);
    } else {
      setServicePlanShowScreenStatus(true);
    }
  };

  const filterData = () => {
    let filteredList = servicePlans.ServicePlansList;

    filteredList = servicePlans.ServicePlansList.filter(function (item) {
      return item.Name.toLowerCase().includes(searchText.toLowerCase());
    });
    return filteredList;
  }

  const removeServicePlanClick = servicePlanId => {
    setActive(true);
    setServicePlanId(servicePlanId);
  };

  const DeleteServicePlan = () => {
    let saveData = servicePlans.SelectedServicePlan;
    saveData.PerformDelete = true;
    saveData.ModelId = ModelId;
    saveData.ModelCategoryId = ModelCategoryId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveData.ModelServicePlanId = servicePlanId;
    saveModelServicePlan(saveData, 'MODELSERVICEPLANSDELETE');
  };

  const handleClose = () => {
    setActive(false);
    setServicePlanId(0);
  };

  const handleConfirm = () => {
    DeleteServicePlan();
    handleClose();
  };

  let messageId = (servicePlans && servicePlans.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELSERVICEPLANSEDIT');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELSERVICEPLANSDELETE');

  const dialogTitle = intl.formatMessage({
    id: 'model.servicePlan.confirmDeleteServicePlans',
    defaultMessage: 'Are you sure you want to delete the service plan?'
  });

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.servicePlan.manageServicePlans" defaultMessage="Manage Service Plans" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)
  const dataObj = Object.values(filterData());

  return (
    <div className={s.modelServicePlan}>
      {servicePlans.isLoading && <LoadingSpinner />}
      {servicePlans.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={servicePlans.showBanner}
          status={servicePlans.isOpSuccessfull}
          successText={messageText}
        />
      )}
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
      <ModelConfigurationContentTemplate selectedPage="manageModelServicePlans" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="model.servicePlan.manageServicePlans" defaultMessage="Manage Service Plans" />
              <span className={s.circle}>
                {servicePlans.ServicePlansList.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                disabled={servicePlans.showAddEditScreen}
                // initialText={filter}
                placeHolderTextId="common.search"
                data-unittest="searchServicePlans"
              />
            </Col>
            <Col sm={6} lg={8}>
              {!ReadOnly && isModelCategoryOwner ? (
                <Button
                  className={s.addbutton}
                  style={WidgetVisibility(userProfileWidgets, 'MODELSERVICEPLANSADD')}
                  onClick={setAddHandler}
                  disabled={servicePlans.showAddEditScreen}
                  data-unittest="addServicePlanButton"
                >
                  + <FormattedMessage id="common.addNew" defaultMessage="ADD NEW" />
                </Button>
              ) : (
                ''
              )}
            </Col>
          </Row>

          <div>
            <Pagination
              data={dataObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={servicePlans.currentPage}>
              <ModelServicePlanListTable
                setEditHandler={setEditHandler}
                ModelCategoryId={ModelCategoryId}
                ModelId={ModelId}
                selectedModelServicePlanId={currentEditId}
                saveModelServicePlan={saveModelServicePlan}
                modelServicePlans={servicePlans}
                describeModelServicePlan={describeModelServicePlan}
                changeModelServicePlan={changeModelServicePlan}
                checklistTemplate={checklistTemplate}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                disableMode={servicePlans.showAddEditScreen}
                disableModeHandler={disableModeHandler}
                addNewRef={addNewRef}
                ReadOnly={ReadOnly}
                removeServicePlan={removeServicePlanClick}
                currentTimezone={currentTimezone}
                isModelCategoryOwner={isModelCategoryOwner}
              />
            </Pagination>
          </div>

        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};

ModelServicePlansList.propTypes = {
  checklistTemplate: PropTypes.object,
  userProfileWidgets: PropTypes.object.isRequired,
  getModelServicePlans: PropTypes.func.isRequired,
  saveModelServicePlan: PropTypes.func.isRequired,
  describeModelServicePlan: PropTypes.func.isRequired,
  changeModelServicePlan: PropTypes.func.isRequired,
  setServicePlanShowScreenStatus: PropTypes.func.isRequired
};

export default injectIntl(ModelServicePlansList);
